import styled, { css } from 'styled-components';

import Button from '../_Button';
import Loader from '../../loader/Loader';

interface StyledButtonProps {
  busy: boolean;
  outline: boolean;
  fullWidth: boolean;
  text: boolean;
  $wrap?: boolean;
}

export const StyledButton = styled(Button)<StyledButtonProps>`
  height: ${({ $wrap }) => $wrap ? 'auto' : '40px'};
  font-size: ${({ theme }) => theme.fontSize.base}px;
  font-weight: 600;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme, busy }) => busy ? 'transparent' : theme.colors.white};
  padding: ${({ $wrap }) => $wrap ? '12px ' : '0 '} 16px;
  border-radius: ${({ theme }) => theme?.TertiaryButton?.borderRadius || theme?.Button?.borderRadius};

  ${({ outline, text }) => !outline && !text && css<StyledButtonProps>`
    box-shadow: 0 1px 0 0 ${({ theme }) => theme.colors.shadowGray1};

    &:hover {
      background: ${({ theme }) => theme.colors.light};
    }

    &:disabled {
      background: ${({ theme }) => theme.colors.disabled};
      font-style: ${({ busy, theme }) => busy ? 'normal' : theme?.Button?.disabledFontStyle};
      cursor: default;
    }

    &:focus-visible, &:focus {
      box-shadow: ${({ theme }) => theme.boxShadows?.focus};
    }

    &:focus:not(&:focus-visible) {
      box-shadow: none;
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.lighter};
    }
  `}

  ${({ outline }) => outline && css<StyledButtonProps>`
    color: ${({ theme }) => theme.colors.primary};
    background: transparent;

    &:after {
      content: '';
      position: absolute;
      display: block;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: 4px;
      border: 1px solid ${({ theme }) => theme.colors.primary};
      pointer-events: none;
    }

    &:disabled {
      background: ${({ theme }) => theme?.TertiaryButton?.disabledBackground || theme.colors.disabled};
      color: ${({ theme, busy }) => busy ? 'transparent' : theme?.TertiaryButton?.disabledColor || theme.colors.white};
      font-style: ${({ busy, theme }) => busy ? 'normal' : theme?.Button?.disabledFontStyle};
      cursor: default;

      &:after {
        border-color: ${({ theme }) => theme?.TertiaryButton?.disabledBorderColor || theme.colors.disabled};
      }
    }

    &:not(:disabled) {
      &:hover {
        color: ${({ theme }) => theme?.TertiaryButton?.hoverColor || theme.colors.primary};

        &:after {
          border-color: ${({ theme }) => theme?.TertiaryButton?.hoverBorderColor || theme.colors.primary};
          border-width: 2px;
        }
      }

      &:focus-visible {
        box-shadow: 0 0 3px 0 ${({ theme }) => theme.colors.lighter};

        &:after {
          border-width: 2px;
        }
      }

      &:focus {
        box-shadow: 0 0 3px 0 ${({ theme }) => theme.colors.lighter};

        &:after {
          border-width: 2px;
        }
      }

      &:focus:not(&:focus-visible) {
        box-shadow: none;
      }

      &:active {
        color: ${({ theme }) => theme?.TertiaryButton?.activeColor || theme.colors.lighter};

        &:after {
          border-color: ${({ theme }) => theme?.TertiaryButton?.borderActiveColor || theme.colors.lighter};
          border-width: 2px;
        }
      }
    }
  `}


  ${({ text }) => text && css<StyledButtonProps>`
    color: ${({ theme }) => theme.colors.primary};
    background: transparent;
    box-shadow: none;
    padding: 0;
    height: auto;

    &:disabled {
      background: ${({ theme }) => theme?.TertiaryButton?.disabledBackground || theme.colors.disabled};
      color: ${({ theme, busy }) => busy ? 'transparent' : theme?.TertiaryButton?.disabledColor || theme.colors.white};
      font-style: ${({ busy, theme }) => busy ? 'normal' : theme?.Button?.disabledFontStyle};
      cursor: default;
    }

    &:not(:disabled) {
      &:hover {
        color: ${({ theme }) => theme?.TertiaryButton?.hoverColor || theme.colors.primary};
        text-decoration: underline;
      }

      &:focus-visible {
        box-shadow: 0 0 3px 0 ${({ theme }) => theme.colors.lighter};
      }

      &:focus {
        box-shadow: 0 0 3px 0 ${({ theme }) => theme.colors.lighter};
      }

      &:focus:not(&:focus-visible) {
        box-shadow: none;
      }

      &:active {
        color: ${({ theme }) => theme?.TertiaryButton?.activeColor || theme.colors.lighter};
      }
    }
  `}

  ${({ fullWidth }) => fullWidth && css`
    width: 100%;
  `}
`;

export const StyledLoaderWrapper = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
`;

export const StyledLoader = styled(Loader)`
  position: absolute;
  top: 11px;
  left: 50%;
  margin-left: -12px;
`;

export const StyledLeftIcon = styled.svg<{ busy: boolean }>`
  margin-right: 8px;
  visibility: ${({ busy }) => busy ? 'hidden' : 'visible'};
`;

export const StyledRightIcon = styled.svg<{ busy: boolean }>`
  margin-left: 8px;
  visibility: ${({ busy }) => busy ? 'hidden' : 'visible'};
`;

export const StyledWrapper = styled.span`
  display: flex;
  align-items: center;
  height: 100%;
`;

export const StyledText = styled.span`
  flex: 1;
`;
